import * as React from "react";
import {
	Box,
	Card,
	Chip,
	FormControl,
	InputLabel,
	Link,
	MenuItem,
	Select,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AdminCampaignBrief, campaignBriefsKeys, CampaignBriefStatus } from "@/hooks/api/use-campaign-briefs";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { CustomTable } from "@/components/customTable";
import { Column, FilterOption } from "@/components/customTable/types";
import { FilterFetchSelect } from "@/components/fetch-select";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { clocksFor, formatDate, StatusChip } from "../campaign-briefs/components";
import { languageLabel, regionLabel, STATUS_LABELS } from "../campaign-briefs/constants";
import { BriefsCalendar } from "./calendar";

const FILTER_STATUSES: CampaignBriefStatus[] = [
	"submitted",
	"in_review",
	"scheduled",
	"live",
	"completed",
	"cancelled",
];

function Paperwork({ label, required, attached }: { label: string; required: boolean; attached: boolean }) {
	if (!required && !attached) return null;
	return (
		<Tooltip
			title={
				required
					? attached
						? `${label} required and attached`
						: `${label} required, not attached yet`
					: `${label} attached`
			}
		>
			<Chip
				label={label}
				size="small"
				variant="outlined"
				color={required && !attached ? "warning" : "success"}
				sx={{ cursor: "help" }}
			/>
		</Tooltip>
	);
}

/** The table of requests, with its status and client filters. */
function ClientRequestsList(): React.JSX.Element {
	const navigate = useNavigate();
	const [status, setStatus] = React.useState<CampaignBriefStatus | "">("");
	const [account, setAccount] = React.useState<{ label: string; value: string }>({ label: "", value: "" });

	const filters = new URLSearchParams({
		...(status ? { status } : {}),
		...(account.value ? { accountId: account.value } : {}),
	}).toString();

	const {
		items: rows,
		isLoading,
		totalItems,
		refetchData,
	} = useCachedTableStateWithUrl<AdminCampaignBrief>({
		queryKey: [...campaignBriefsKeys.adminLists(), filters],
		endpoint: `/admin/campaign-briefs${filters ? `?${filters}` : ""}`,
	});

	const columns: Column[] = React.useMemo(
		() => [
			{
				header: { id: "submitted", label: "Received", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "submittedAt",
				getCellValue: (row: AdminCampaignBrief) =>
					row.submittedAt ? dayjs(row.submittedAt).format("D MMM, HH:mm") : "-",
			},
			{
				header: { id: "account", label: "Client", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "accountName",
				getCellValue: (row: AdminCampaignBrief) => (
					<Box>
						<Typography variant="body2">{row.accountName ?? "-"}</Typography>
						<Typography variant="caption" color="text.secondary">
							{row.submittedByName ?? ""}
						</Typography>
					</Box>
				),
			},
			{
				header: { id: "title", label: "Campaign", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "title",
				getCellValue: (row: AdminCampaignBrief) => (
					<Link component="button" variant="subtitle2" onClick={() => navigate(`/client-requests/${row.briefId}`)}>
						{row.title}
					</Link>
				),
			},
			{
				header: { id: "dial", label: "First dial", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "dialDate",
				getCellValue: (row: AdminCampaignBrief) => (
					<Box>
						<Typography variant="body2">{formatDate(row.dialDate)}</Typography>
						<Typography variant="caption" color="text.secondary">
							{clocksFor(row.regions)}
							{row.redialDates.length > 0
								? ` · ${row.redialDates.length} redial${row.redialDates.length === 1 ? "" : "s"}`
								: ""}
						</Typography>
					</Box>
				),
			},
			{
				header: { id: "where", label: "Regions / languages", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "regions",
				getCellValue: (row: AdminCampaignBrief) => (
					<Box>
						<Typography variant="body2">{row.regions.map((code) => regionLabel(code)).join(", ")}</Typography>
						<Typography variant="caption" color="text.secondary">
							{row.languages.map((tag) => languageLabel(tag)).join(", ")}
						</Typography>
					</Box>
				),
			},
			{
				header: { id: "agent", label: "Agent", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "agentChoice",
				getCellValue: (row: AdminCampaignBrief) =>
					row.agentChoice === "existing" ? (
						(row.agentName ?? "Existing")
					) : (
						<Chip label="New agent" size="small" color="info" />
					),
			},
			{
				header: { id: "paperwork", label: "PO / SOW", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "poRequired",
				getCellValue: (row: AdminCampaignBrief) => (
					<Stack direction="row" spacing={1}>
						<Paperwork label="PO" required={row.poRequired} attached={row.hasPo} />
						<Paperwork label="SOW" required={row.sowRequired} attached={row.hasSow} />
						{!row.poRequired && !row.sowRequired && !row.hasPo && !row.hasSow ? "-" : null}
					</Stack>
				),
			},
			{
				header: { id: "status", label: "Status", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "status",
				getCellValue: (row: AdminCampaignBrief) => <StatusChip status={row.status} />,
			},
		],
		[navigate]
	);

	return (
		<>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
				<FormControl sx={{ minWidth: 200 }}>
					<InputLabel id="status-filter-label">Status</InputLabel>
					<Select
						labelId="status-filter-label"
						label="Status"
						value={status}
						onChange={(event) => setStatus(event.target.value as CampaignBriefStatus | "")}
					>
						<MenuItem value="">All statuses</MenuItem>
						{FILTER_STATUSES.map((value) => (
							<MenuItem key={value} value={value}>
								{STATUS_LABELS[value]}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<Box sx={{ minWidth: 280 }}>
					<FilterFetchSelect
						label="Client"
						showInputLabel
						value={account as FilterOption}
						placeholder="All clients"
						onChange={(selected: FilterOption | string) =>
							setAccount(
								typeof selected === "string"
									? { label: selected, value: selected }
									: { label: selected.label ?? "", value: (selected.value as string) ?? "" }
							)
						}
						getOptionsUrl="/accounts"
						fetchLabel="name"
						fetchValue="id"
					/>
				</Box>
			</Stack>

			<TableAnimation delay={0}>
				<Card>
					<CustomTable
						key={filters}
						columns={columns}
						data={rows}
						refetchData={refetchData}
						isLoading={isLoading}
						totalItems={totalItems}
					/>
				</Card>
			</TableAnimation>
		</>
	);
}

/** Every account's campaign requests, for AdaptiveX staff: as a list, or by dial date on a calendar. */
export function ClientRequests(): React.JSX.Element {
	// In the URL, so going back from a request returns to the calendar rather than the list.
	const [searchParams, setSearchParams] = useSearchParams();
	const view = searchParams.get("view") === "calendar" ? "calendar" : "list";
	const showView = (next: "list" | "calendar") => {
		const params = new URLSearchParams(searchParams);
		if (next === "calendar") params.set("view", "calendar");
		else params.delete("view");
		setSearchParams(params, { replace: true });
	};

	return (
		<Box
			sx={{
				maxWidth: "var(--Content-maxWidth)",
				m: "var(--Content-margin)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={4}>
				<Stack direction="row" spacing={2} sx={{ alignItems: "flex-start", justifyContent: "space-between" }}>
					<Box>
						<Typography variant="h4">Client requests</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
							Campaign requests submitted from every client account
						</Typography>
					</Box>
					<ToggleButtonGroup
						exclusive
						size="small"
						value={view}
						onChange={(_, next: "list" | "calendar" | null) => next && showView(next)}
						aria-label="View"
					>
						<ToggleButton value="list">List</ToggleButton>
						<ToggleButton value="calendar">Calendar</ToggleButton>
					</ToggleButtonGroup>
				</Stack>

				{view === "calendar" ? <BriefsCalendar scope="admin" /> : <ClientRequestsList />}
			</Stack>
		</Box>
	);
}
